import { ColumnDef } from "@tanstack/react-table";
import Checkbox from "@/components/Checkbox";

/**
 * Configuration options for the selection column
 *
 * @property size - Size of the selection column in pixels (default: 40)
 * @property className - Additional className for both body cells and headers (default: "pr-0")
 * @property enableSorting - Whether to enable sorting on this column (default: false)
 */
export interface SelectionColumnOptions {
  size?: number;
  className?: string;
  enableSorting?: boolean;
}

/**
 * Builds a standardized selection column for tables with row selection.
 *
 * This helper eliminates boilerplate by providing a consistent selection column
 * definition that works with TanStack Table's row selection API.
 *
 * @example
 * ```tsx
 * import { buildBulkSelectionColumn } from "@/components/Table";
 *
 * const columns = [
 *   buildBulkSelectionColumn(),
 *   { accessorKey: "name", header: "Name" },
 *   { accessorKey: "email", header: "Email" }
 * ];
 * ```
 *
 * @example With custom options
 * ```tsx
 * const columns = [
 *   buildBulkSelectionColumn({ size: 50, className: "pl-4" }),
 *   // ... other columns
 * ];
 * ```
 *
 * @param options - Optional configuration for the selection column
 * @returns A ColumnDef with header and cell checkboxes configured
 */
export function buildBulkSelectionColumn<TData extends { id: string }>(
  options: SelectionColumnOptions = {}
): ColumnDef<TData, any> {
  const { size = 40, className = "pr-0", enableSorting = false } = options;

  return {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        name="select-all"
        checked={table.getIsAllRowsSelected()}
        indeterminate={table.getIsSomeRowsSelected()}
        onChange={(checked) => table.toggleAllRowsSelected(checked)}
      />
    ),
    cell: ({ row }) => (
      <div onClick={(e) => e.stopPropagation()}>
        <Checkbox
          name={`select-row-${row.id}`}
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={(checked) => row.toggleSelected(checked)}
        />
      </div>
    ),
    size,
    minSize: size,
    maxSize: size,
    enableSorting,
    meta: {
      className,
      headerClassName: className
    }
  };
}
